import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Lavorazione, LavorazioneEsolver, Risorsa, SearchResult} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {OrganizzazioniService} from './organizzazioni.service';
import {SaveMessage} from '../_helpers/save-message';

const cachePrefix = 'archiconnect-v2-';
const emptyLavorazione: Lavorazione = {
  organizzazioneId: 0,
  id: 0,
  codice: '',
  descrizione: '',
  isActive: false
};

@Injectable({providedIn: 'root'})
export class LavorazioniService {
  public lastLavorazioneCreated: BehaviorSubject<Lavorazione> = new BehaviorSubject<Lavorazione>(null);
  public lastLavorazioneUpdated: BehaviorSubject<Lavorazione> = new BehaviorSubject<Lavorazione>(null);
  public lastLavorazioneDeleted: BehaviorSubject<Lavorazione> = new BehaviorSubject<Lavorazione>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  public searchLavorazione: BehaviorSubject<Lavorazione> = new BehaviorSubject(emptyLavorazione);
  public myLavorazioni: BehaviorSubject<Lavorazione[]> = new BehaviorSubject([]);
  private hubConnection: HubConnection;

  constructor(
    private organizzazioniService: OrganizzazioniService,
    private http: HttpClient,
    private saveMessage: SaveMessage) {
    const lavorazioneJson = localStorage.getItem(cachePrefix + 'lavorazione');
    if (lavorazioneJson !== undefined && lavorazioneJson !== 'undefined') {
      const lavorazione = JSON.parse(localStorage.getItem(cachePrefix + 'lavorazione')) || emptyLavorazione;
      this.searchLavorazione.next(lavorazione);
    }
  }

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/lavorazioni`, this.hubState);

    this.hubConnection.on('SendCreated', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Nuova lavorazione creata.');
        this.lastLavorazioneCreated.next(risorsa);
      }
    });

    this.hubConnection.on('SendUpdated', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Lavorazione aggiornata.');
        this.lastLavorazioneUpdated.next(risorsa);
      }
    });

    this.hubConnection.on('SendDeleted', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Lavorazione eliminata.');
        this.lastLavorazioneDeleted.next(risorsa);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  setLavorazione(lavorazione: Lavorazione) {
    if (lavorazione !== undefined) {
      this.searchLavorazione.next(lavorazione);
      localStorage.setItem(cachePrefix + 'lavorazione', JSON.stringify(lavorazione));
    }
  }

  resetLavorazione() {
    this.searchLavorazione.next(emptyLavorazione);
    localStorage.setItem(cachePrefix + 'lavorazione', JSON.stringify(emptyLavorazione));
  }

  async getLavorazioniFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean, excludeInactive: boolean) {
    return firstValueFrom(
      this.http.post<SearchResult<Lavorazione>>(`${environment.localApiUrl}api/Lavorazioni/LavorazioniFiltered?orgId=${orgId}`,
        {search, page, count, sortField, sortAsc, excludeInactive})
    );
  }

  async getLavorazioneAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/Lavorazione?orgId=${orgId}&code=${code}`));
  }

  async createLavorazioneAsync(lavorazione: Lavorazione) {
    return firstValueFrom(this.http.post<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/CreateLavorazione`, lavorazione));
  }

  async updateLavorazioneAsync(lavorazione: Lavorazione) {
    return firstValueFrom(this.http.post<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/UpdateLavorazione`, lavorazione));
  }

  async deleteLavorazioneAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/DeleteLavorazioni?orgId=${orgId}&code=${code}`));
  }

  async getLavorazioniOfAccountAsync(orgId: number, accountId: number) {
    return firstValueFrom(this.http.get<SearchResult<Lavorazione>>(`${environment.localApiUrl}api/Lavorazioni/LavorazioniByAccountId?` +
      `orgId=${orgId}&accountId=${accountId}`));
  }

  setMyLavorazioni(orgId: number) {
    firstValueFrom(
      this.http.get<SearchResult<Lavorazione>>(`${environment.localApiUrl}api/Lavorazioni/MyLavorazioni?orgId=${orgId}`)
    ).then((_lavorazioni: SearchResult<Lavorazione>) => this.myLavorazioni.next(_lavorazioni.result));
  }

  async addLavorazioneToAccountAsync(accountId: number, orgId: number, lavorazioneId: number, alsoRisorse: boolean) {
    return firstValueFrom(this.http
      .post<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/AddLavorazioneToAccount`,
        {accountId, orgId, lavorazioneId, alsoRisorse}));
  }

  async removeLavorazioneFromAccountAsync(accountId: number, orgId: number, lavorazioneId: number) {
    return firstValueFrom(this.http
      .get<Lavorazione>(`${environment.localApiUrl}api/Lavorazioni/RemoveLavorazioneFromAccount?` +
        `accountId=${accountId}&orgId=${orgId}&lavorazioneId=${lavorazioneId}`));
  }

  getLavorazioniEsolver(orgId: number) {
    return firstValueFrom(this.http.get<SearchResult<LavorazioneEsolver>>(`${environment.localApiUrl}api/Esolver/Lavorazioni/${orgId}`));
  }
}
